import { cn } from "@/lib/utils";
import type { PublicSponsor } from "@/lib/sponsors.functions";

type Size = "sm" | "md" | "lg";

const sizeStyles: Record<Size, { card: string; img: string; width: number; height: number }> = {
  sm: {
    card: "h-[72px] md:h-[84px] px-3 md:px-4",
    img: "max-h-10 md:max-h-12",
    width: 140,
    height: 64,
  },
  md: {
    card: "h-[110px] md:h-[130px] px-4 md:px-6",
    img: "max-h-14 md:max-h-16",
    width: 200,
    height: 96,
  },
  lg: {
    card: "h-[170px] md:h-[210px] px-6 md:px-10",
    img: "max-h-24 md:max-h-32",
    width: 320,
    height: 160,
  },
};

export function SponsorLogoCard({
  sponsor,
  size = "md",
  className,
  href,
}: {
  sponsor: PublicSponsor;
  size?: Size;
  className?: string;
  href?: string;
}) {
  if (!sponsor.logo_url) return null;
  const s = sizeStyles[size];

  const isDark = sponsor.bg_variant === "dark";
  const isTransparent = sponsor.bg_variant === "transparent";

  const cardBg = isTransparent
    ? "bg-transparent border-transparent"
    : isDark
      ? "bg-night-deep border-gold/20"
      : "bg-cream border-gold/15";

  const imgFilter = isDark && sponsor.invert_on_dark ? "invert brightness-0" : "";

  const cardClasses = cn(
    "group flex w-full min-w-0 shrink-0 items-center justify-center rounded-2xl border transition-all duration-300",
    !isTransparent && "shadow-[0_1px_2px_rgba(0,0,0,0.25)] hover:shadow-[0_8px_24px_-8px_var(--shadow-glow)] hover:-translate-y-0.5",
    cardBg,
    s.card,
    className,
  );

  const img = (
    <img
      src={sponsor.logo_url}
      alt=""
      width={s.width}
      height={s.height}
      loading="lazy"
      className={cn("w-auto max-w-full object-contain", s.img, imgFilter)}
    />
  );

  // Only make clickable for safe http(s) URLs.
  const isLink = href && /^https?:\/\//i.test(href);

  if (isLink) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${sponsor.company} (opent in nieuw tabblad)`}
        className={cn(
          cardClasses,
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-gold",
        )}
      >
        {img}
      </a>
    );
  }

  return (
    <div aria-label={`${sponsor.company} logo`} className={cardClasses}>
      <img
        src={sponsor.logo_url}
        alt={`${sponsor.company} logo`}
        width={s.width}
        height={s.height}
        loading="lazy"
        className={cn("w-auto max-w-full object-contain", s.img, imgFilter)}
      />
    </div>
  );
}
